/**
 * Drawer — slide-over panel from the right edge.
 *
 * Same accessibility guarantees as Modal (portal, backdrop, focus trap,
 * Escape to close, return focus on close) but rendered as a right-side
 * panel instead of a centered dialog.
 *
 * Dark mode (U3): semantic tokens carry NO `.dark` overrides in this app
 * (see `FlowTable.tsx`'s convention note), so every one of this file's class
 * strings needs an explicit `dark:` variant — `Modal.tsx` has none of these,
 * which is exactly the bug U3 fixes by moving `BrowseAllModal` onto this
 * primitive instead. Fixing it HERE (not just in the one caller) means every
 * future `Drawer` consumer inherits correct dark styling for free.
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { pushEscapeLayer } from './escape-stack';
import { FLOW_PORTAL_ATTR } from '../flow/useClickOutside';

interface DrawerProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * Replaces the default title/close-button row entirely — the caller owns
   * the whole header, including its own close affordance (mirrors `Modal`'s
   * `header` prop). `title` is ignored when this is supplied.
   */
  header?: React.ReactNode;
  /** Width class — default w-[480px] */
  widthClassName?: string;
  className?: string;
  /** Render into a specific DOM element instead of document.body.
   *  When set, uses absolute positioning (contained) instead of fixed (viewport). */
  portalTarget?: HTMLElement | null;
}

export function Drawer({
  open,
  title,
  onClose,
  children,
  footer,
  header,
  widthClassName = 'w-[480px] max-w-full',
  className = '',
  portalTarget,
}: DrawerProps) {
  const isInline = portalTarget !== undefined;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevActiveRef = useRef<HTMLElement | null>(null);

  // Escape key — R2: registers on the shared escape stack (`escape-stack.ts`)
  // rather than its own raw `document` listener, so a nested layered surface
  // (e.g. a `ResponsivePopover`-based dialog opened from inside this Drawer)
  // owns a single Escape keypress instead of both listeners firing on it.
  useEffect(() => {
    if (!open) return;
    return pushEscapeLayer(onClose);
  }, [open, onClose]);

  // Body scroll lock + focus trap
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    if (!isInline) document.body.style.overflow = 'hidden';
    prevActiveRef.current = (document.activeElement as HTMLElement | null) || null;

    // Focus first focusable or container
    const node = containerRef.current;
    try {
      const focusables = node?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusables?.length ? focusables[0] : node;
      first?.focus();
    } catch {}

    // Tab trap
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const n = containerRef.current;
      if (!n) return;
      // R2 — if focus currently sits inside a genuinely separate portaled
      // overlay (e.g. `ViewNameDialog`'s `ResponsivePopover`, portaled to
      // `document.body` and therefore NOT a descendant of this Drawer's own
      // `containerRef`), back off entirely: let that overlay's own focus
      // handling (if any) own Tab, rather than yanking focus back into the
      // Drawer as if it had "escaped" a boundary it was never inside.
      const cur = document.activeElement as HTMLElement | null;
      const portalAncestor = cur?.closest(`[${FLOW_PORTAL_ATTR}]`);
      if (portalAncestor && !n.contains(portalAncestor)) return;
      const els = Array.from(
        n.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('disabled'));
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey) {
        if (cur === first || !n.contains(cur)) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (cur === last) {
          first.focus();
          e.preventDefault();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      if (!isInline) document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
      if (prevActiveRef.current) {
        try { prevActiveRef.current.focus(); } catch {}
        prevActiveRef.current = null;
      }
    };
  }, [open, isInline]);

  if (!open) return null;

  const content = (
    <div
      className={`${isInline ? 'absolute' : 'fixed'} inset-0 z-50 flex justify-end`}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20 dark:bg-black/40"
        data-testid="drawer-backdrop"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        ref={containerRef}
        data-testid="drawer-panel"
        className={`relative z-10 h-full bg-bg-raised dark:bg-nightshade-900 shadow-2xl flex flex-col
          ${widthClassName}
          animate-slide-in-right
          ${className}`}
      >
        {/* Header */}
        {header ?? (
          <div className="px-5 py-4 border-b border-border-secondary dark:border-nightshade-700 flex items-center justify-between flex-shrink-0">
            <h3 className="text-base font-semibold text-content-primary dark:text-nightshade-100">{title}</h3>
            <button
              className="p-1.5 rounded-md text-icon-subtle hover:text-content-tertiary hover:bg-bg-secondary dark:text-icon-subtle dark:hover:text-nightshade-100 dark:hover:bg-nightshade-800 transition-colors"
              onClick={onClose}
              aria-label="Close"
              data-testid="drawer-close"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-5 py-3 border-t border-border-secondary bg-bg-secondary dark:border-nightshade-700 dark:bg-nightshade-800 flex items-center justify-end gap-2 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, portalTarget ?? document.body);
}
